import { useLocation } from "react-router-dom";
import { ArticlePageSkeleton } from "./ArticlePageSkeleton";
import AuthBootSkeleton from "./AuthBootSkeleton";
import DesktopAuthBootSkeleton from "./DesktopAuthBootSkeleton";
import DiscoveryBootSkeleton from "./DiscoveryBootSkeleton";
import GPTConversationBootSkeleton from "./GPTConversationBootSkeleton";
import JuisciGPTBootSkeleton from "./JuisciGPTBootSkeleton";
import PlaylistBootSkeleton from "./PlaylistPageSkeleton";
import RoomDiscoveryBootSkeleton from "./RoomDiscoveryBootSkeleton";
import VisualAbstractLibraryBootSkeleton from "./VisualAbstractLibrarySkeleton";
import DesktopBootSkeleton, {
  type DesktopBootPage,
} from "./DesktopBootSkeleton";
import type { HomeDestination } from "../../domains/app/hooks/useHomeSurface";
import {
  isDesktopBrowserLayout,
  isDesktopShell,
} from "../../common/hooks/useIsDesktopView";
import { isChromelessRoute } from "../app/layout/AppLayout";

/**
 * First-paint placeholder for the cold-boot window while a returning user's
 * persisted Firebase token is validated server-side (the `accounts:lookup`
 * round-trip). Rendered by the boot gate in AppRoutes in place of a blank
 * screen — see its call site for why this only applies to returning users.
 *
 * The boot gate fires for ANY deep-linked route, so the silhouette must match
 * the destination rather than always showing Discovery. This dispatches on the
 * pathname to the page-shaped skeleton (article / playlist / room discovery),
 * falling back to the main Discovery shell. `rootDestination` covers the one
 * case the pathname can't: a root boot, whose destination is resolved by
 * `getInitialRoute`. Each target is a pure render (no queries, no auth reads),
 * so mounting it cannot mis-render as logged-out.
 *
 * The desktop shell answers first, with its own silhouettes (the sidebar and
 * the page): see `desktopBootPage` for the routes it leaves to the ones below.
 * The desktop browser's front door comes next: it has no welcome page, its
 * door is `DesktopAuthPage` (see `isDesktopAuthDoor`).
 *
 * `front-door` is the third root answer, and it is not a home: a visitor who
 * has never signed in on this install is heading for `/welcome`, not for
 * anyone's feed. It is a separate member rather than a `null` home, because a
 * home nobody has is not the same fact as a destination that is not a home.
 */
export type BootRootDestination = HomeDestination["kind"] | "front-door";

export default function BootSkeleton({
  rootDestination,
}: {
  rootDestination: BootRootDestination;
}): React.JSX.Element {
  const { pathname } = useLocation();

  if (isDesktopShell()) {
    const page = desktopBootPage(pathname, rootDestination);
    if (page !== null) return <DesktopBootSkeleton page={page} />;
  }

  if (
    isDesktopBrowserLayout() &&
    isDesktopAuthDoor(pathname, rootDestination)
  ) {
    return <DesktopAuthBootSkeleton />;
  }

  // Article: /post/:slug and /room/post/:roomId/:slug
  if (pathname.startsWith("/post/") || pathname.startsWith("/room/post/")) {
    return <ArticlePageSkeleton />;
  }

  // Playlist detail: /playlist/:id, /room/playlist/:roomId/:id,
  // /profile/playlist/:id. `/profile/playlist/create` is the editor, not a
  // detail screen, so it falls through to the default shell.
  if (
    pathname.startsWith("/playlist/") ||
    pathname.startsWith("/room/playlist/") ||
    (pathname.startsWith("/profile/playlist/") &&
      pathname !== "/profile/playlist/create")
  ) {
    return <PlaylistBootSkeleton />;
  }

  // The visual-abstract library — a grid of poster cards under a content
  // header, with no navbar. Deliberately matched BEFORE any prefix that could
  // swallow it, and distinct from `/visual-abstract/…` (singular), whose
  // preview and report screens are not this shape.
  if (pathname === "/visual-abstracts") {
    return <VisualAbstractLibraryBootSkeleton />;
  }

  // A room's Discovery page has its own header/sections, distinct from the
  // main feed shell.
  if (pathname.startsWith("/room/discovery/")) {
    return <RoomDiscoveryBootSkeleton />;
  }

  // Auth funnel: /welcome, /login, /signup and their sub-paths get a
  // bottom-anchored column skeleton (logo → title → CTAs), not the feed shell.
  if (isAuthFunnelPath(pathname)) {
    return <AuthBootSkeleton />;
  }

  // Root boot resolves through `getInitialRoute`: a pinned-room user reaches
  // their room unless a per-room rule sends them to the JuisciGPT composer (the
  // B2C home), which is where everyone else goes. Neither is the main feed, so
  // neither may fall through to its shell. The caller resolves which — the
  // answer needs this device's last settled destination, not just the pin.
  if (pathname === "/") {
    switch (rootDestination) {
      case "gpt":
        return <JuisciGPTBootSkeleton />;
      case "pinned-room":
        return <RoomDiscoveryBootSkeleton />;
      case "front-door":
        return <AuthBootSkeleton />;
      default:
        throw new Error(rootDestination satisfies never);
    }
  }

  // An EXISTING conversation, deep-linked or reopened from the history list:
  // /gpt/chat/conversation/:id and /room/gpt/chat/conversation/:roomId/:id. It
  // has neither the greeting nor the conversation starters of the landing, so
  // it gets its own silhouette.
  if (
    pathname.startsWith("/gpt/chat/conversation/") ||
    pathname.startsWith("/room/gpt/chat/conversation/")
  ) {
    return <GPTConversationBootSkeleton />;
  }

  // Any other GPT route — the landing (`/gpt/chat/new`) and its room twin.
  if (pathname.startsWith("/gpt/") || pathname.startsWith("/room/gpt/")) {
    return <JuisciGPTBootSkeleton />;
  }

  return <DiscoveryBootSkeleton />;
}

function isAuthFunnelPath(pathname: string): boolean {
  return (
    pathname.startsWith("/welcome") ||
    pathname.startsWith("/login") ||
    pathname.startsWith("/signup")
  );
}

/**
 * The paths where a desktop browser draws `DesktopAuthPage`, the same test as
 * the router's: `/signup` and `/login` exactly (their sub-paths are other
 * pages), `/welcome` (it redirects to `/signup`), and the root for a visitor
 * heading for the front door.
 */
function isDesktopAuthDoor(
  pathname: string,
  rootDestination: BootRootDestination
): boolean {
  if (pathname === "/") return rootDestination === "front-door";
  return (
    pathname === "/welcome" || pathname === "/signup" || pathname === "/login"
  );
}

/**
 * The desktop page a boot draws (Figma "Transverse 07"), chosen by the path:
 * a room shows the room, Explore shows Explore, and everything else shows the
 * JuisciGPT landing, the desktop home. Null where the desktop shell draws no
 * sidebar: a visitor with no session (the front door), the auth funnel and the
 * routes that take the whole screen keep the silhouettes above.
 */
function desktopBootPage(
  pathname: string,
  rootDestination: BootRootDestination
): DesktopBootPage | null {
  if (rootDestination === "front-door") return null;
  if (isAuthFunnelPath(pathname) || isChromelessRoute(pathname)) return null;
  if (pathname === "/") {
    switch (rootDestination) {
      case "gpt":
        return "gpt";
      case "pinned-room":
        return "room";
      default:
        throw new Error(rootDestination satisfies never);
    }
  }
  // A room's own conversation is a conversation, not the room's page.
  if (pathname.startsWith("/room/gpt/")) return "gpt";
  if (pathname.startsWith("/room/") || pathname.startsWith("/rooms"))
    return "room";
  if (pathname.startsWith("/discovery")) return "explore";
  return "gpt";
}
